import { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Table, Button } from 'reactstrap';
import { toast } from 'react-toastify';
import ModalRecibo from '../ModalRecibo';
import ModalLoader from '../../../../layouts/modals/ModalLoader';
import ModalClickable from '../../../../layouts/modals/ModalClickable';
import matriculacionApiService from '../../../../services/http/matriculacionApiService';
import documentosApiService from '../../../../services/http/documentosApiService';
import { getItem, STORAGE_KEYS } from '../../../../services/storage';
import logger from '../../../../services/logger';
import usePasarelaActiva from '../../../../hooks/usePasarelaActiva';
import useSoloLecturaMatriculas from '../../../../hooks/useSoloLecturaMatriculas';
import { labelPasarela, PASARELAS } from '../../../../helpers/pasarelas';
import FilaRecibo from './FilaRecibo';

const Recibos = ({ setListadoRecibos, listadoRecibos, idMatricula }) => {
  const userSession = getItem(STORAGE_KEYS.USER);
  const enrollmentSession = getItem(STORAGE_KEYS.ENROLLMENT);
  const soloLectura = useSoloLecturaMatriculas();

  const [recibos, setRecibos] = useState([]);
  const [isAllChecked, setIsAllChecked] = useState(false);
  const [isLoadingMetodosPago, setIsLoadingMetodosPago] = useState(true);
  const [isLoadingEstados, setIsLoadingEstados] = useState(true);
  const [isVisible, setIsVisible] = useState(false);
  const [isProceedingLoading, setIsProceedingLoading] = useState(false);
  const [isMailLoading, setIsMailLoading] = useState(false);
  const [isCambioTarjetaLoading, setIsCambioTarjetaLoading] = useState(false);
  const [showButtonPasarela, setShowButtonPasarela] = useState(false);
  const { pasarela } = usePasarelaActiva(enrollmentSession?.headquarter);
  const pasarelaLabel = labelPasarela(pasarela);
  const [totalRecibos, setTotalRecibos] = useState(0);
  const [metodosPago, setMetodosPago] = useState([]);
  const [estados, setEstados] = useState([]);
  const [reloadRecibos, setReloadRecibos] = useState(true);
  const { empresaId, matriculaId } = useParams();
  const [savingStatus, setSavingStatus] = useState({});
  const [isCobroManualLoading, setIsCobroManualLoading] = useState(false);
  const [cobrarManualModal, setCobrarManualModal] = useState({
    show: false,
    message: '',
    isError: false,
  });

  useEffect(() => {
    const fetchMetodosPago = async () => {
      setIsLoadingMetodosPago(true);
      try {
        const dataMetodosPago = await matriculacionApiService.cargarMetodosPago({
          headquarter: 'CEDESCA',
        });
        setMetodosPago(dataMetodosPago);
      } catch (error) {
        setMetodosPago([]);
      } finally {
        setIsLoadingMetodosPago(false);
      }
    };
    const fetchEstados = async () => {
      setIsLoadingEstados(true);
      try {
        const dataEstadosRecibo = await matriculacionApiService.cargarEstadosRecibo({
          headquarter: 'CEDESCA',
        });
        setEstados(dataEstadosRecibo);
      } catch (error) {
        setEstados([]);
      } finally {
        setIsLoadingEstados(false);
      }
    };
    fetchMetodosPago();
    fetchEstados();
  }, []);

  useEffect(() => {
    const fetchRecibo = async () => {
      try {
        const dataRecibo = await matriculacionApiService.mostrarRecibos({
          headquarter: enrollmentSession.headquarter,
          id: idMatricula,
        });
        const a = [...dataRecibo];
        let precioTotalRecibos = 0;
        a.forEach((e) => {
          precioTotalRecibos += parseFloat(e.Neto);
        });
        setTotalRecibos(precioTotalRecibos);
        setRecibos(a);
      } catch (error) {
        setTotalRecibos(0);
        setRecibos([]);
      } finally {
        setReloadRecibos(false);
      }
    };

    if (reloadRecibos) {
      fetchRecibo();
    }

    setShowButtonPasarela(
      recibos.map((recibo) => recibo.Forma_de_pago).includes(pasarelaLabel),
    );
  }, [enrollmentSession?.headquarter, idMatricula, recibos, reloadRecibos, pasarelaLabel]);

  const metodosPagoConHistoricos = useMemo(() => {
    const idsConocidos = new Set(metodosPago.map((m) => Number(m.id)));
    const extras = [];
    const yaAniadidos = new Set();
    recibos.forEach((r) => {
      const id = Number(r.idPago);
      if (Number.isNaN(id)) return;
      if (idsConocidos.has(id)) return;
      if (yaAniadidos.has(id)) return;
      if (!r.Forma_de_pago) return;
      yaAniadidos.add(id);
      extras.push({ id, descripcion: r.Forma_de_pago });
    });
    return [...metodosPago, ...extras];
  }, [metodosPago, recibos]);

  const handleReciboChange = (reciboId, isChecked) => {
    const updatedListadoRecibos = isChecked
      ? [...listadoRecibos, reciboId]
      : listadoRecibos.filter((id) => id !== reciboId);
    setListadoRecibos(updatedListadoRecibos);
  };

  const marcarTodos = () => {
    const pendienteReciboIds = recibos
      .filter((recibo) => recibo.Estado === 'Pendiente')
      .map((recibo) => recibo.NUM_Recibo);
    setListadoRecibos(pendienteReciboIds);
    setIsAllChecked(true);
  };

  const desmarcarTodos = () => {
    setListadoRecibos([]);
    setIsAllChecked(false);
  };

  const deleteIds = async () => {
    try {
      await matriculacionApiService.deleteRecibo({
        headquarter: enrollmentSession.headquarter,
        idRecibos: listadoRecibos,
      });
    } catch (error) {
      logger.error('Eliminar recibo/s =>', error);
    } finally {
      setReloadRecibos(true);
      setListadoRecibos([]);
    }
  };

  const resendEmail = async (numRecibo) => {
    const recibo = recibos.find((r) => r.NUM_Recibo === numRecibo);
    const esRedsys = (recibo?.Forma_de_pago || '').toUpperCase() === 'REDSYS';
    setIsMailLoading(true);
    try {
      if (esRedsys) {
        const res = await matriculacionApiService.enviarEmailPagoRedsys({
          headquarter: empresaId,
          numRecibo,
        });
        const destinatarios = (res?.paraEmail || []).join(', ');
        toast.success(
          destinatarios
            ? `Email de pago enviado a ${destinatarios}`
            : 'Email de pago enviado',
        );
      } else {
        await matriculacionApiService.reenviarPagoInicial({
          headquarter: empresaId,
          numMatricula: matriculaId,
          reciboId: numRecibo,
        });
      }
    } catch (error) {
      logger.error('Enviar enlace de pago =>', error);
    } finally {
      setIsMailLoading(false);
    }
  };

  const reenviarEmailMetodoPago = async () => {
    setIsMailLoading(true);
    await matriculacionApiService.reenviarEmailMetodoPago({
      headquarter: empresaId,
    });
    setIsMailLoading(false);
  };

  const enviarCambioTarjeta = async () => {
    setIsCambioTarjetaLoading(true);
    try {
      await matriculacionApiService.enviarCambioTarjetaRedsys({
        headquarter: empresaId,
        numMatricula: idMatricula,
      });
      toast.success('Enviado email de cambio de tarjeta Redsys');
    } catch (error) {
      logger.error('Enviar cambio de tarjeta Redsys =>', error);
    } finally {
      setIsCambioTarjetaLoading(false);
    }
  };

  const handleSaveRecibo = async (recibo) => {
    let fechaCobro = recibo.Fecha_cobro;
    if (!fechaCobro || fechaCobro === '0000-00-00') {
      const hoy = new Date();
      const [fechaHoy] = hoy.toISOString().split('T');
      fechaCobro = fechaHoy;
    }

    setSavingStatus((prev) => ({ ...prev, [recibo.NUM_Recibo]: 'loading' }));

    try {
      await matriculacionApiService.editRecibo({
        headquarter: empresaId,
        idRecibo: recibo.NUM_Recibo,
        data: { ...recibo, Fecha_cobro: fechaCobro },
      });
      setSavingStatus((prev) => ({ ...prev, [recibo.NUM_Recibo]: 'success' }));
      setReloadRecibos(true);
    } catch (error) {
      logger.error('Error al guardar recibo:', error);
      setSavingStatus((prev) => ({ ...prev, [recibo.NUM_Recibo]: 'error' }));
    }
  };

  const openModal = () => setIsVisible(true);

  const handleFechaCobroChange = (numRecibo, nuevaFecha) => {
    setRecibos((prev) =>
      prev.map((r) => (r.NUM_Recibo === numRecibo ? { ...r, Fecha_cobro: nuevaFecha } : r)),
    );
  };

  const downloadFile = async (e) => {
    setIsProceedingLoading(true);
    try {
      const dataBlob = await documentosApiService.exportarReciboPDF(
        { headquarter: enrollmentSession.headquarter, reciboId: e },
        { headquarter: enrollmentSession.headquarter },
      );
      const blobFinal = dataBlob.slice(0, dataBlob.size, 'application/pdf');
      const url = URL.createObjectURL(blobFinal);
      window.open(url, '_blank');
    } catch (error) {
      // ignore
    } finally {
      setIsProceedingLoading(false);
    }
  };

  const handleCobrarManual = async (numRecibo) => {
    const recibo = recibos.find((r) => r.NUM_Recibo === numRecibo);
    const esRedsys = (recibo?.Forma_de_pago || '').toUpperCase() === 'REDSYS';
    setIsCobroManualLoading(true);
    try {
      if (esRedsys) {
        await matriculacionApiService.cobrarReciboManualRedsys(
          {
            headquarter: empresaId,
            num_recibo: numRecibo,
          },
          { silent: true },
        );
      } else {
        await matriculacionApiService.cobrarReciboManual(
          {
            NUM_RECIBO: numRecibo,
            headquarter: empresaId,
          },
          { silent: true },
        );
      }
      setCobrarManualModal({
        show: true,
        message: 'Recibo cobrado exitosamente',
        isError: false,
      });
      setReloadRecibos(true);
    } catch (error) {
      const data = error.response?.data;
      const errorMessage =
        data?.motivo || data?.error || data?.displayMessage || 'Error al cobrar el recibo';
      setCobrarManualModal({
        show: true,
        message: errorMessage,
        isError: true,
      });
    } finally {
      setIsCobroManualLoading(false);
    }
  };

  const deshabilitarPorEmail =
    userSession?.profileObj?.email?.toLowerCase?.() === 'ajimenez@cedetecgroup.com' || soloLectura;

  return (
    <>
      {isProceedingLoading ? <ModalLoader header="Generando documento" /> : null}
      {isMailLoading ? <ModalLoader header="Reenviando enlace de pago" /> : null}
      {isCambioTarjetaLoading ? (
        <ModalLoader header="Enviando email de cambio de tarjeta" />
      ) : null}
      {isCobroManualLoading ? <ModalLoader header="Procesando cobro manual" /> : null}
      {cobrarManualModal.show && (
        <ModalClickable
          header={cobrarManualModal.isError ? 'Error' : 'Éxito'}
          body={cobrarManualModal.message}
          isVisible={cobrarManualModal.show}
          setIsVisible={() => setCobrarManualModal({ show: false, message: '', isError: false })}
        />
      )}
      {isVisible && (
        <ModalRecibo
          isVisible={isVisible}
          setIsVisible={setIsVisible}
          matricula={idMatricula}
          setReloadRecibos={setReloadRecibos}
        />
      )}
      {isAllChecked && !isLoadingEstados && !isLoadingMetodosPago && listadoRecibos?.length > 0 ? (
        <Button className="me-1 mb-2" onClick={desmarcarTodos} disabled={soloLectura}>
          <i className="bi bi-check-square" style={{ marginRight: '8px' }} />
          Desmarcar todos
        </Button>
      ) : (
        <Button className="me-1 mb-2" onClick={marcarTodos} disabled={soloLectura}>
          <i className="bi bi-check-square-fill" style={{ marginRight: '8px' }} />
          Marcar todos
        </Button>
      )}
      <Button className="me-1 mb-2" color="success" onClick={openModal} disabled={soloLectura}>
        Añadir
      </Button>
      <Button
        className="me-1 mb-2"
        color="danger"
        disabled={listadoRecibos.length === 0 || soloLectura}
        onClick={deleteIds}
      >
        Borrar
      </Button>
      {showButtonPasarela && pasarela !== PASARELAS.REDSYS && (
        <Button className="me-1 mb-2" onClick={reenviarEmailMetodoPago} disabled={soloLectura}>
          Cambiar método pago {pasarelaLabel}
        </Button>
      )}
      {pasarela === PASARELAS.REDSYS && (
        <Button className="me-1 mb-2" onClick={enviarCambioTarjeta} disabled={soloLectura}>
          Cambiar tarjeta Redsys
        </Button>
      )}

      <Table
        responsive
        borderless
        style={{ fontSize: '13px', borderCollapse: 'separate', borderSpacing: '0 1em' }}
      >
        <thead>
          <tr>
            <th>ID recibo</th>
            <th>ID factura</th>
            <th>Concepto</th>
            <th>Fecha emisión</th>
            <th>Fecha vencimiento</th>
            <th>Fecha cobro</th>
            <th>Neto</th>
            <th>Bruto</th>
            <th>Descuento</th>
            <th>Tipo</th>
            <th>Forma de pago</th>
            <th>Estado</th>
            <th>Acciones</th>
          </tr>
        </thead>
        <tbody>
          {recibos.map((r) => (
            <FilaRecibo
              key={r.codigo}
              recibo={r}
              listadoRecibos={listadoRecibos}
              onReciboChange={handleReciboChange}
              setListadoRecibos={setListadoRecibos}
              onFechaCobroChange={handleFechaCobroChange}
              metodosPago={metodosPagoConHistoricos}
              isLoadingMetodosPago={isLoadingMetodosPago}
              estados={estados}
              isLoadingEstados={isLoadingEstados}
              setRecibos={setRecibos}
              savingStatus={savingStatus}
              onSaveRecibo={handleSaveRecibo}
              onDownloadFile={downloadFile}
              onResendEmail={resendEmail}
              onCobrarManual={handleCobrarManual}
              deshabilitarPorEmail={deshabilitarPorEmail}
              pasarelaLabel={pasarelaLabel}
            />
          ))}
        </tbody>
      </Table>
      <span className="text-danger">Importe total de los recibos: {totalRecibos.toFixed(2)} €</span>
    </>
  );
};

Recibos.propTypes = {
  listadoRecibos: PropTypes.array,
  setListadoRecibos: PropTypes.func,
  idMatricula: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default Recibos;
